import { useState } from 'react'

/**
 * Creates a ref-storage object to be used on uncontrolled form components.<br/>
 * Opted for ref-based forms to avoid re-renders on simple, unvalidated forms.
 *
 * @example
 *   const [inputs, storage, value] = storeRefs()
 *   const doSubmit = () => window.alert(`Hello ${value('name')}!`) //or inputs.name.value
 *   return (
 *     <form onSubmit={doSubmit}>
 *       <label for="name">Name:</label>
 *       <input id="name" ref={storage} defaultValue="World"/>
 *       <button onClick={() => value('name', '')}>clear</button>
 *     </form>
 *   )
 *
 * @see  https://lorenstewart.me/2017/06/10/react-js-forms-using-refs/ Reasoning, samples and a link to controlled components
 * @see useFieldState
 *
 * @returns {[object, function(ref: object), function(id: string, value: any = undefined)]}
 */
export const storeRefs = () => {
	const refs    = {}
	const storage = ref => {
		if (ref) { //if it's null, it means the component is unmounting
			refs[ref.id] = ref
		}
	}
	const value   = (id, value = undefined) => {
		if (typeof value !== 'undefined') { //this is the setter
			refs[id].value = value
		}

		//otherwise, we do the getter part
		return ['checkbox', 'radio'].includes(refs[id].type)? refs[id].checked : refs[id].value
	}
	return [refs, storage, value]
}

/**
 * Simpler state hook for form fields - sets directly what's in the event's target.
 * You'll probably need this if you have other behaviors in the form that depend on the state of this field. If that's
 * not the case, {@link storeRefs} can save you a couple of state boilerplate.
 * @example
 *   const [enabled, onChangeEnabled] = useFieldState(false)
 *   const [,, textInput] = useFieldState(false)
 *   return <>
 *     <input type="checkbox" checked={enabled} onChange={onChangeEnabled}/> or...
 *     <input type="text" {...textInput}/>
 *
 * @see storeRefs
 * @param initial {any}
 * @param options {object<boolean>}:
 *   - bool: Gathers the final state from the input's "checked", instead of "value"
 *   - lowerCase: turns any letter into lower-case upon input
 * @returns {[*, function({target: *}), object]} The third element can be unpacked directly in the field
 */
export const useFieldState = (initial, { bool = false, lowerCase = false } = {}) => {
	const [state, setter] = useState(initial)

	const fieldSetter = ({ target }) => setter(
		bool? target.checked :
			(lowerCase? target.value.toLowerCase() : target.value),
	)

	const unpackable = {
		[bool? 'checked' : 'value']: state,
		onChange                   : fieldSetter,
	}

	return [state, fieldSetter, unpackable]
}
